<template>
  <footer class="bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 py-16 relative overflow-hidden rounded-lg">
    <!-- Background animated particles -->
    <div class="absolute inset-0 z-0 opacity-20">
      <div class="particles-container">
        <div v-for="i in 20" :key="i" 
             class="particle" 
             :style="{
               left: `${Math.random() * 100}%`,
               top: `${Math.random() * 100}%`,
               animationDelay: `${Math.random() * 5}s`,
               width: `${Math.random() * 4 + 1}px`,
               height: `${Math.random() * 4 + 1}px`
             }">
        </div>
      </div>
    </div>
    
    <!-- Glowing accent line -->
    <div class="relative z-10">
      <div class="h-px w-full max-w-6xl mx-auto bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-70 mb-12"></div>
    
      <div class="container mx-auto px-6">
        <div class="max-w-6xl mx-auto">
          <!-- Top Section -->
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <!-- Logo and Tagline -->
            <div class="lg:col-span-4 flex flex-col items-center lg:items-start relative">
              <div class="absolute -z-10 w-40 h-40 rounded-full bg-blue-500 opacity-20 blur-3xl top-0 left-0 transform -translate-x-1/2 -translate-y-1/2"></div>
              
              <Link href="/" aria-label="Balan.ai Home" class="group mb-6 transition duration-300">
                <ApplicationLogo width="220" height="auto" class="max-w-[220px] w-full filter drop-shadow-lg group-hover:drop-shadow-2xl transition-all duration-300" />
              </Link>
              
              <p class="text-gray-400 text-lg text-center lg:text-left mb-6 font-light leading-relaxed">
                Discover the best AI tools for your workflow and stay updated with the latest in AI technology.
              </p>
              
            </div>
            
            <!-- Navigation Links with Glow Effects -->
            <div class="lg:col-span-8">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
                <!-- Quick Links with hover glow effect -->
                <div class="footer-links-section">
                  <h3 class="footer-heading">
                    <span>Quick Links</span>
                  </h3>
                  <ul class="space-y-3">
                    <li>
                      <Link href="/about" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>About</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                    <li>
                      <Link href="/faq" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>FAQ</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                    <li>
                      <Link href="/how-it-works" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>How It Works</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                  </ul>
                </div>
                
                <!-- Legal with hover glow effect -->
                <div class="footer-links-section">
                  <h3 class="footer-heading">
                    <span>Legal</span>
                  </h3>
                  <ul class="space-y-3">
                    <li>
                      <Link href="/terms-and-conditions" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>Terms & Conditions</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                    <li>
                      <Link href="/privacy-policy" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>Privacy Policy</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                  </ul>
                </div>
                
                <!-- Contribute with hover glow effect -->
                <div class="footer-links-section">
                  <h3 class="footer-heading">
                    <span>Contribute</span>
                  </h3>
                  <ul class="space-y-3">
                    <li>
                      <Link href="/submit-ai" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>Submit AI Tool</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                    <li>
                      <Link href="/contact" class="footer-link group">
                        <span class="footer-link-icon"></span>
                        <span>Contact Us</span>
                        <span class="footer-link-line"></span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Bottom Section with Copyright -->
          <div class="pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between relative">
            <div class="text-gray-500 text-sm mb-4 sm:mb-0 group">
              <span class="group-hover:text-gray-300 transition-colors duration-300">&copy; 2025 Balan.ai. All rights reserved.</span>
            </div>
            
            <!-- Back to top button with pulse effect -->
            <button 
              @click="scrollToTop" 
              class="back-to-top-button"
              aria-label="Back to top"
            >
              <span class="back-to-top-pulse"></span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
              </svg>
              <span>Back to top</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
  import { Link } from '@inertiajs/vue3';
  import ApplicationLogo from '@/Components/ApplicationLogo.vue';

  // Function to scroll to top of page with smooth behavior
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
</script>

<style scoped>
  /* Particle animations for background */
  .particles-container {
    height: 100%;
    width: 100%;
    position: relative;
  }
  
  .particle {
    position: absolute;
    background-color: rgba(59, 130, 246, 0.5);
    border-radius: 50%;
    animation: float 15s infinite ease-in-out;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) translateX(0);
      opacity: 0.2;
    }
    25% {
      transform: translateY(-30px) translateX(15px);
      opacity: 0.3;
    }
    50% {
      transform: translateY(-15px) translateX(30px);
      opacity: 0.5;
    }
    75% {
      transform: translateY(-30px) translateX(-15px);
      opacity: 0.3;
    }
  }
  
  /* Footer section headings */
  .footer-heading {
    @apply text-white font-semibold text-lg mb-5 pb-2 relative inline-block;
    border-bottom: 1px solid rgba(59, 130, 246, 0.3);
  }
  
  .footer-heading::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 30%;
    height: 2px;
    background: linear-gradient(90deg, rgba(59, 130, 246, 1), rgba(59, 130, 246, 0));
  }
  
  /* Footer link styling with animation */
  .footer-link {
    @apply text-gray-400 transition-colors relative flex items-center;
    padding-left: 1rem;
  }
  
  .footer-link-icon {
    @apply absolute w-1.5 h-1.5 rounded-full left-0 bg-gray-600 transition-all duration-300;
  }
  
  .footer-link:hover {
    @apply text-blue-400;
  }
  
  .footer-link:hover .footer-link-icon {
    @apply bg-blue-500;
    box-shadow: 0 0 8px rgba(59, 130, 246, 0.8);
  }
  
  .footer-link-line {
    @apply absolute left-0 bottom-0 w-0 h-0.5 bg-blue-500 transition-all duration-300 opacity-0;
  }
  
  .footer-link:hover .footer-link-line {
    @apply w-full opacity-100;
  }
  
  /* Back to top button styling */
  .back-to-top-button {
    @apply inline-flex items-center px-4 py-2 rounded-full transition-all duration-300 relative;
    background: linear-gradient(90deg, rgba(29, 78, 216, 0.2), rgba(67, 56, 202, 0.3));
    color: rgba(191, 219, 254, 0.8);
    backdrop-filter: blur(4px);
    border: 1px solid rgba(59, 130, 246, 0.3);
  }
  
  .back-to-top-button:hover {
    background: linear-gradient(90deg, rgba(37, 99, 235, 0.4), rgba(79, 70, 229, 0.5));
    color: rgba(219, 234, 254, 1);
    transform: translateY(-2px);
    box-shadow: 0 6px 15px -3px rgba(37, 99, 235, 0.4);
  }
  
  .back-to-top-pulse {
    @apply absolute inset-0 rounded-full;
    background: linear-gradient(90deg, rgba(37, 99, 235, 0.4), rgba(79, 70, 229, 0.5));
    z-index: -1;
    opacity: 0;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.4;
    }
    50% {
      transform: scale(1.1);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 0;
    }
  }
  
  /* Social media links styling */
  .footer-social-link {
    @apply w-10 h-10 rounded-full flex items-center justify-center text-gray-300 transition-all duration-300 relative;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  }
  
  .footer-social-link:hover {
    @apply text-white transform scale-110;
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.5);
  }
  
  .social-tooltip {
    @apply absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs py-1 px-2 rounded opacity-0 transition-opacity duration-300 pointer-events-none;
    min-width: 60px;
    text-align: center;
  }
  
  .social-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #1f2937 transparent transparent transparent;
  }
  
  .footer-social-link:hover .social-tooltip {
    @apply opacity-100;
  }
  
  /* Improved accessibility focus states */
  a:focus, button:focus {
    outline: none;
    @apply ring-2 ring-blue-400 ring-offset-1 ring-offset-gray-900 rounded;
  }
  
  footer {
    animation: fadeIn 0.5s ease-out;
    box-shadow: inset 0 1px 0 0 rgba(255, 255, 255, 0.05);
  }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  /* Improved section styling */
  .footer-links-section {
    @apply bg-gray-900 bg-opacity-40 p-5 rounded-lg transition-all duration-300;
    backdrop-filter: blur(8px);
    border: 1px solid rgba(55, 65, 81, 0.5);
  }
  
  .footer-links-section:hover {
    @apply bg-opacity-60;
    box-shadow: 0 8px 25px -5px rgba(0, 0, 0, 0.5);
    transform: translateY(-2px);
    border-color: rgba(59, 130, 246, 0.3);
  }
  
  /* Improved mobile responsiveness */
  @media (max-width: 640px) {
    .container {
      padding-left: 1rem;
      padding-right: 1rem;
    }
    
    .back-to-top-button {
      width: 100%;
      justify-content: center;
      margin-top: 1rem;
    }
    
    .footer-links-section {
      padding: 1rem;
    }
  }
  
  /* Support reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
    
    .particle {
      display: none;
    }
    
    .back-to-top-pulse {
      display: none;
    }
  }
</style>